import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrgSuspensionStatus } from '../actioncreators/actioncreators';
import { setOrgSuspensionStatus } from '../actions/loanAction';

// Create the context
const SuspensionContext = createContext({
  isSuspended: false,
  suspensionReason: null,
  suspendedAt: null,
  isLoading: true,
  refreshSuspensionStatus: () => {},
});

// Provider component
export const SuspensionProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  // Get suspension status from Redux
  const orgSuspensionStatus = useSelector((state) => state.orgSuspensionStatus);
  const loginResponse = useSelector((state) => state.loginResponse);

  // Check if user is Super Admin (Super Admins should not be affected by suspension)
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' ||
                       loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';

  // Function to fetch and update suspension status
  const refreshSuspensionStatus = useCallback(async () => {
    // Never call the API on public / auth pages. SuspensionProvider wraps the
    // whole app, so a stale loginResponse left in the store would otherwise
    // fire org-suspension-status on the login page and 401.
    const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
    if (!path.startsWith('/home')) {
      setIsLoading(false);
      return;
    }

    // Super Admins are not affected by suspension
    if (isSuperAdmin) {
      dispatch(setOrgSuspensionStatus(false, null, null));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetchOrgSuspensionStatus();

      if (response?.data?.businessStatusCode === 2) {
        const data = response.data.data;
        dispatch(setOrgSuspensionStatus(
          data.is_suspended || false,
          data.suspension_reason || null,
          data.suspended_at || null
        ));
      }
    } catch (error) {
      console.error('Error fetching suspension status:', error);
      // On error, assume not suspended to not block users
      dispatch(setOrgSuspensionStatus(false, null, null));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, isSuperAdmin]);

  // Fetch suspension status on mount and when login response changes
  useEffect(() => {
    if (loginResponse?.user_id && !isSuperAdmin) {
      refreshSuspensionStatus();
    } else if (isSuperAdmin) {
      dispatch(setOrgSuspensionStatus(false, null, null));
      setIsLoading(false);
    }
  }, [loginResponse?.user_id, refreshSuspensionStatus, isSuperAdmin, dispatch]);

  const value = {
    isSuspended: orgSuspensionStatus?.isSuspended || false,
    suspensionReason: orgSuspensionStatus?.suspensionReason || null,
    suspendedAt: orgSuspensionStatus?.suspendedAt || null,
    isLoading,
    refreshSuspensionStatus,
  };

  return (
    <SuspensionContext.Provider value={value}>
      {children}
    </SuspensionContext.Provider>
  );
};

// Custom hook to use suspension context
export const useSuspension = () => {
  const context = useContext(SuspensionContext);
  if (context === undefined) {
    throw new Error('useSuspension must be used within a SuspensionProvider');
  }
  return context;
};

// HOC to disable component when org is suspended
export const withSuspensionCheck = (WrappedComponent) => {
  return function WithSuspensionCheckComponent(props) {
    const { isSuspended } = useSuspension();

    // Pass isSuspended as a prop to the wrapped component
    return <WrappedComponent {...props} orgSuspended={isSuspended} />;
  };
};

// Utility function to get button props when org is suspended
export const getSuspendedButtonProps = (isSuspended, originalOnClick) => {
  if (isSuspended) {
    return {
      disabled: true,
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
      },
      title: 'Actions are disabled because your organization is suspended',
      style: { opacity: 0.5, cursor: 'not-allowed' },
    };
  }
  return {
    onClick: originalOnClick,
  };
};

export default SuspensionContext;
